import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Driver } from '@hero/schema';
import { useDriverListApi, useDriverApi } from '@hero/api-hooks';
import { SelectAsync, type SelectionValue, type SelectAsyncMode } from '../../../app/Select';
import { DriverCreateModal } from '../Modal';
import { DriverIcon, TruckIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import {
  AvailabilityChip,
  driverStatusChip,
  formatPlateForDisplay,
  SelectRowCheck,
} from '../../../app/Select/SelectAsync/selectorEntityRowUtils';

export type DriverSelectionValue = SelectionValue<Driver>;

export interface DriverSelectorProps {
  selectedDrivers?: DriverSelectionValue;
  onDriversChange: (drivers: Driver[] | undefined) => void;
  onDriverSave?: (driver: Driver) => void;
  allowCreateNew?: boolean;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  required?: boolean;
  mode?: SelectAsyncMode;
}

export const DriverSelector = React.memo(function DriverSelector({
  selectedDrivers,
  onDriversChange,
  onDriverSave,
  allowCreateNew: allowCreateNewProp,
  disabled = false,
  placeholder,
  label,
  required,
  mode = 'single',
}: DriverSelectorProps) {
  const { t, i18n } = useTranslation(['m-driver', 'm-vehicle', 'c-main']);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const dir = i18n.dir();

  const driverListQuery = useDriverListApi({});
  const driverSingleQuery = useDriverApi(undefined);

  const allowCreateNew = allowCreateNewProp !== false;

  const openCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const handleDriverSave = useCallback(
    (driver: Driver) => {
      onDriverSave?.(driver);
      closeModal();
    },
    [onDriverSave, closeModal],
  );

  const renderDriverRow = useMemo(
    () =>
      (driver: Driver, { isSelected }: { isSelected: boolean }) => {
        const vehicle = driver.vehicle;
        const chip = driverStatusChip(t, driver.status);
        const vehicleLabel = vehicle
          ? `${formatPlateForDisplay(vehicle.plate)} · ${t(`m-vehicle:types.${vehicle.type}`, {
              defaultValue: String(vehicle.type),
            })}`
          : t('m-driver:selector.no_vehicle');

        return (
          <div dir={dir} className="flex items-start gap-2 w-full group min-w-0">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-primary/10 p-1.5">
              <DriverIcon className="size-5 text-primary" size="small" />
            </div>
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="min-w-0 truncate text-sm font-medium text-gray-900">
                  {driver.name}
                </span>
                <span className="truncate text-xs text-gray-500">
                  <span dir="ltr">{driver.phone}</span>
                </span>
                <div
                  className={cn(
                    'flex min-w-0 items-center gap-2 text-xs',
                    vehicle ? 'text-gray-400' : 'text-red-500',
                  )}>
                  <span
                    className={cn(
                      'flex size-5 shrink-0 items-center justify-center rounded',
                      vehicle ? 'bg-gray-100' : 'bg-red-50',
                    )}>
                    <TruckIcon
                      className={cn('size-3', vehicle ? 'text-gray-400' : 'text-red-500')}
                    />
                  </span>
                  <span className="truncate">{vehicleLabel}</span>
                </div>
              </div>
              <AvailabilityChip label={chip.label} tone={chip.tone} />
            </div>
            {mode === 'single' && <SelectRowCheck isSelected={isSelected} />}
          </div>
        );
      },
    [t, mode, dir],
  );

  const renderDriverTrigger = useMemo(
    () => (selected: Driver | Driver[] | null) => {
      if (!selected || Array.isArray(selected)) return null;
      const driver = selected;
      const vehicle = driver.vehicle;
      const chip = driverStatusChip(t, driver.status);
      const sub =
        vehicle != null ? formatPlateForDisplay(vehicle.plate) : t('m-driver:selector.no_vehicle');

      return (
        <div dir={dir} className="flex min-w-0 flex-1 items-center gap-2">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-primary/10">
            <DriverIcon className="size-4 text-primary" size="small" />
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="min-w-0 truncate text-sm font-medium text-gray-900">
                {driver.name}
              </span>
              <span className="truncate text-xs text-gray-500">
                <span dir="ltr">{driver.phone}</span>
                {' · '}
                <span dir="auto">{sub}</span>
              </span>
            </div>
            <AvailabilityChip label={chip.label} tone={chip.tone} />
          </div>
        </div>
      );
    },
    [t, dir],
  );

  return (
    <div className="space-y-3">
      <SelectAsync<Driver>
        value={selectedDrivers}
        listQuery={driverListQuery}
        singleQuery={driverSingleQuery}
        onChangeSelect={onDriversChange}
        getItemId={d => d._id}
        placeholder={placeholder || t('selector.placeholder')}
        searchPlaceholder={t('selector.search_placeholder')}
        emptyMessage={t('selector.no_drivers')}
        loadingMessage={t('c-main:async_operations.loading')}
        createNewLabel={t('c-main:crud_buttons.create_new')}
        onCreateNew={openCreateModal}
        allowCreateNew={allowCreateNew}
        allowEdit={false}
        mode={mode}
        disabled={disabled}
        label={label}
        required={required}
        renderOption={renderDriverRow}
        renderTrigger={mode === 'single' ? renderDriverTrigger : undefined}
      />
      {allowCreateNew && (
        <DriverCreateModal
          isOpen={isCreateModalOpen}
          onClose={closeModal}
          onDriverSaved={handleDriverSave}
        />
      )}
    </div>
  );
});
